import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, message } from 'antd';
import { ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { SMART_PASTE_MAX_CHARS } from '@/api/smartPasteService';
import { useSmartPasteParse } from '@/hooksApi/useSmartPaste';
import type { SmartPasteOrder, SmartPasteResponse } from '@/types/smartPaste';
import { readSmartPasteError, type SmartPasteError } from './smartPasteError';
import { blockedText, warningLines } from './smartPasteMessages';
import type { SmartPasteGap } from './smartPasteGaps';
import SmartPasteMark from './SmartPasteMark';
import SmartPasteGapPanel from './SmartPasteGapPanel';

interface SmartPasteCardProps {
  /**
   * Called with the parsed order to pre-fill into the form. Returns what the
   * fill left for the merchant to do by hand — the page answers rather than
   * this card, because only the page knows what the form looks like afterwards.
   *
   * Deliberately NOT `| void`: a page that forgets to return the gaps leaves
   * the merchant with a silently half-filled form, and that is a compile error
   * worth having rather than a bug worth finding in the browser.
   */
  onApply: (order: SmartPasteOrder) => SmartPasteGap[];
}

/**
 * Smart Paste entry point at the top of Create New Order.
 *
 * Merchants get their orders as free-form WhatsApp/Instagram messages. Paste one
 * into the modal and the form below fills itself. There is no preview step on
 * purpose — the filled form IS the preview, and the merchant reviews and submits
 * it as usual, so a wrong guess costs a correction, never a bad order.
 */
const SmartPasteCard = ({ onApply }: SmartPasteCardProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<SmartPasteError | null>(null);
  const [retryIn, setRetryIn] = useState(0);
  // What Smart Paste couldn't fill in, field by field. Lives outside the modal
  // because the modal closes on a successful fill — the list has to sit next to
  // the form it applies to.
  const [gaps, setGaps] = useState<SmartPasteGap[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { mutate: parse } = useSmartPasteParse();

  // Focus on open so a plain Cmd/Ctrl+V lands in the right place.
  useEffect(() => {
    if (open) setTimeout(() => textareaRef.current?.focus(), 80);
  }, [open]);

  // Live countdown for a rate-limit reset, so "try again later" is a real number.
  useEffect(() => {
    if (retryIn <= 0) return;
    const id = setInterval(() => setRetryIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [retryIn]);

  const close = () => {
    setOpen(false);
    setText('');
    setError(null);
    setParsing(false);
  };

  const tooLong = text.length > SMART_PASTE_MAX_CHARS;
  const canParse = text.trim().length > 0 && !tooLong && !parsing && retryIn === 0;

  // An over-long message outranks whatever else is on screen: it is the reason
  // the button is dead, and it has to say so. A red character counter on its
  // own left merchants trimming the wrong end and blaming "missing city".
  const shownError: SmartPasteError | null = tooLong
    ? { kind: 'too_long', length: text.length }
    : error;

  const handleParse = () => {
    if (!canParse) return;
    setError(null);
    setParsing(true);

    parse(text.trim(), {
      onSuccess: (response: SmartPasteResponse) => {
        setParsing(false);
        const orders = response?.object?.orders ?? [];

        if (orders.length === 0) {
          setError({ kind: 'empty' });
          return;
        }

        // Fill the first order we can actually use. A message with several
        // orders still fills one form — say so rather than silently dropping
        // the rest.
        const usable = orders.find((order) => !order.blocked && order.mapped);
        if (!usable) {
          setError({
            kind: 'blocked',
            reasons: orders[0]?.block_reasons ?? [],
            code: orders[0]?.block_code ?? null,
            // A blocked order still has warnings worth reading — "we couldn't
            // read the phone number" explains what to fix by hand.
            warnings: warningLines(orders[0]?.warnings, t),
          });
          return;
        }

        // `?? []` is not dead code: the strict return type only binds
        // callers TypeScript can see, and a page handing back undefined used
        // to crash this card — and the form under it — on `.length`.
        setGaps(onApply(usable) ?? []);
        close();
        message.success(
          orders.length > 1
            ? String(t('smart_paste_applied_multi', { count: orders.length }))
            : String(t('smart_paste_applied')),
        );
      },
      onError: (err: unknown) => {
        setParsing(false);
        const parsedError = readSmartPasteError(err);
        setError(parsedError);
        if (parsedError.kind === 'rate_limited') setRetryIn(parsedError.retryInSeconds);
      },
    });
  };

  const errorMessage = () => {
    const error = shownError;
    if (!error) return null;
    if (error.kind === 'rate_limited') {
      return retryIn > 0
        ? String(t('smart_paste_error_rate_limited_in', { seconds: retryIn }))
        : String(t('smart_paste_error_rate_limited'));
    }
    if (error.kind === 'unavailable') return String(t('smart_paste_error_unavailable'));
    if (error.kind === 'empty') return String(t('smart_paste_error_empty'));
    if (error.kind === 'too_long') {
      return String(
        t('smart_paste_error_too_long', { length: error.length, max: SMART_PASTE_MAX_CHARS }),
      );
    }
    if (error.kind === 'blocked') return blockedText(error.code, error.reasons, t);
    return error.message || String(t('smart_paste_error_generic'));
  };

  /** Extra detail under a blocked message — the same warnings a fill would show. */
  const errorDetails = shownError?.kind === 'blocked' ? (shownError.warnings ?? []) : [];

  return (
    <>
      <button
        type='button'
        onClick={() => setOpen(true)}
        data-testid='smart-paste-open'
        className={`group relative w-full overflow-hidden rounded-2xl border border-solid border-sd-primary/40 bg-gradient-to-r rtl:bg-gradient-to-l from-sd-primary/10 to-transparent px-5 py-4 text-start cursor-pointer transition-shadow hover:shadow-md ${
          gaps.length ? 'mb-3' : 'mb-6'
        }`}
      >
        <div className='flex items-center gap-4'>
          <SmartPasteMark size='md' />
          <span className='min-w-0 flex-1'>
            <span className='flex items-center gap-2'>
              <span className='text-[15px] font-bold text-sd-foreground'>
                {String(t('smart_paste_title'))}
              </span>
              <span className='rounded-full bg-sd-foreground px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sd-primary'>
                {String(t('smart_paste_badge'))}
              </span>
            </span>
            <span className='mt-0.5 block text-[13px] text-sd-muted-foreground'>
              {String(t('smart_paste_subtitle'))}
            </span>
          </span>
          <span className='hidden sm:inline-flex shrink-0 items-center gap-1.5 rounded-md bg-sd-primary px-3 py-2 text-sm font-medium text-sd-primary-foreground'>
            {String(t('smart_paste_cta_open'))}
            <ArrowRight className='h-4 w-4 rtl:rotate-180' strokeWidth={2} />
          </span>
        </div>
      </button>

      <SmartPasteGapPanel gaps={gaps} onDismiss={() => setGaps([])} />

      <Modal
        open={open}
        onCancel={close}
        footer={null}
        centered
        width={560}
        destroyOnClose
        maskClosable={!parsing}
        className='sd-scope sd-font'
        // The app resets `.ant-modal-content` padding to 0 (shadcn-migration.css),
        // so the body supplies its own — without it the textarea hugs the modal
        // edge. The shared mask is very light; darken it so the form behind
        // doesn't read through this one.
        styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.45)' } }}
      >
        <div className='flex flex-col gap-4 p-6'>
          <div className='flex items-center gap-3'>
            <SmartPasteMark size='sm' />
            <div className='min-w-0'>
              <p className='m-0 text-base font-semibold text-sd-foreground'>
                {String(t('smart_paste_title'))}
              </p>
              <p className='m-0 mt-0.5 text-[13px] text-sd-muted-foreground'>
                {String(t('smart_paste_subtitle'))}
              </p>
            </div>
          </div>

          <div className='relative'>
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              readOnly={parsing}
              rows={6}
              data-testid='smart-paste-textarea'
              placeholder={String(t('smart_paste_placeholder'))}
              // resize-none on purpose: a user-resizable box escapes the modal.
              className={`block w-full max-w-full box-border resize-none rounded-xl border border-solid bg-sd-input-bg p-3 text-sm leading-relaxed text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring ${
                tooLong ? 'border-red-400' : 'border-sd-border'
              } ${parsing ? 'opacity-60' : ''}`}
            />
            {parsing ? (
              <span className='pointer-events-none absolute inset-0 overflow-hidden rounded-xl'>
                <span className='absolute inset-x-0 h-10 animate-sp-scan bg-gradient-to-b from-transparent via-sd-primary/40 to-transparent' />
              </span>
            ) : null}
          </div>

          {shownError ? (
            <div role='alert' className='rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700'>
              <p className='m-0'>{errorMessage()}</p>
              {errorDetails.length ? (
                <ul className='m-0 mt-1.5 ps-4 list-disc space-y-0.5'>
                  {errorDetails.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          <div className='flex items-center justify-between gap-3'>
            <span
              className={`text-[12px] ${tooLong ? 'text-red-500' : 'text-sd-muted-foreground'}`}
              dir='ltr'
            >
              {text.length} / {SMART_PASTE_MAX_CHARS}
            </span>
            {parsing ? (
              <span className='flex items-center gap-2 text-[13px] font-medium text-sd-foreground'>
                <Loader2 className='h-3.5 w-3.5 animate-spin' strokeWidth={2} />
                {String(t('smart_paste_parsing'))}
              </span>
            ) : (
              <div className='flex items-center gap-2'>
                <button
                  type='button'
                  onClick={close}
                  className='h-10 rounded-lg border border-solid border-sd-border bg-transparent px-4 text-sm font-semibold text-sd-foreground hover:bg-sd-muted cursor-pointer transition-colors'
                >
                  {String(t('cancel'))}
                </button>
                <button
                  type='button'
                  onClick={handleParse}
                  disabled={!canParse}
                  data-testid='smart-paste-parse'
                  className='inline-flex h-10 items-center gap-2 rounded-lg border-0 bg-sd-primary px-4 text-sm font-semibold text-sd-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-opacity'
                >
                  <Sparkles className='h-4 w-4' strokeWidth={2} />
                  {String(t('smart_paste_cta_parse'))}
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SmartPasteCard;
